import { Component, OnInit } from "@angular/core";
import { AuthService } from "src/app/auth.service";
import { DataServiceService } from "src/app/data-service.service";
import { User } from "../../model/login";
import * as moment from "moment";
import {
  ADVERSE_MEDIA_CATEGORIES,
  getAdverseMediaTotalReturnedCount,
} from "../../utils/adverse-media-utils";

interface MediaArticleRow {
  categoryKey: string;
  categoryLabel: string;
  headline: string;
  language: string;
  date: string;
  sourceLink: string;
  sourceLabel: string;
}

interface MediaCategorySection {
  key: string;
  label: string;
  emptyMessage: string;
  hits: number;
  articles: MediaArticleRow[];
}

@Component({
  selector: "app-new-print-media-body",
  templateUrl: "./new-print-media-body.component.html",
  styleUrls: ["./new-print-media-body.component.scss"],
})
export class NewPrintMediaBodyComponent implements OnInit {
  searchStringText = "";
  searchedName = "";
  typeOfSearch = "";
  userDetails: User;
  isLoading = true;
  hongKongDT = "";
  entityDD = "00";
  entityMM = "00";
  entityYYYY = "0000";
  entityGender = "NO";
  dailyCounterString = "0000";
  todayName = moment(new Date()).format("YYYYMMDD");
  showSuccessMsg = false;
  isSavingRecord = false;
  reportDateShow = "";
  adverseMediaData: any;
  totalHits = 0;
  categorySections: MediaCategorySection[] = [];

  constructor(
    private dataService: DataServiceService,
    private authService: AuthService,
  ) {}

  ngOnInit(): void {
    this.reportDateShow = moment().format("DD/MM/YYYY HH:mm:ss");
    this.loadReportData();
    this.searchStringText =
      this.typeOfSearch != "advance"
        ? `NEW QUICK SEARCH RESULTS FOR ${this.searchedName}`
        : `NEW ADVANCE SEARCH RESULTS FOR ${this.searchedName}`;

    this.getUserDetails().then(() => {
      this.isLoading = false;
    });
  }

  private loadReportData() {
    const historyStored = localStorage.getItem("mediaReportHistoryData");
    const historyRecord = historyStored ? JSON.parse(historyStored) : null;

    if (historyRecord) {
      this.applyHistoryRecord(historyRecord);
    } else {
      this.applyCurrentSearch();
    }

    this.buildMediaRows();
  }

  private applyHistoryRecord(record: any) {
    this.searchedName = record.searchedName || record.name || "";
    this.typeOfSearch = record.typeOfSearch || "";
    this.hongKongDT = record.hongKongDT || "";
    this.entityDD = record.entityDD || "00";
    this.entityMM = record.entityMM || "00";
    this.entityYYYY = record.entityYYYY || "0000";
    this.entityGender = record.entityGender || "NO";
    this.dailyCounterString = record.dailyCounterString || "0000";
    this.todayName = record.todayName || this.todayName;
    this.adverseMediaData =
      record.adverseMediaData || {
        timestamp: record.timestamp,
        news_exposures: record.news_exposures || {},
      };
  }

  private applyCurrentSearch() {
    this.searchedName =
      localStorage.getItem("adverseMediaSearchedName") ||
      localStorage.getItem("searchedName") ||
      "";
    this.typeOfSearch = localStorage.getItem("searchType") || "";
    this.hongKongDT = localStorage.getItem("searchDateAndTime") || "";
    this.getDataFromLocalStorage();

    const stored = localStorage.getItem("adverseMediaData");
    this.adverseMediaData = stored ? JSON.parse(stored) : null;
  }

  private getDataFromLocalStorage() {
    this.entityDD = localStorage.getItem("dd") ? localStorage.getItem("dd") : "00";
    this.entityMM = localStorage.getItem("mm") ? localStorage.getItem("mm") : "00";
    this.entityYYYY = localStorage.getItem("yyyy")
      ? localStorage.getItem("yyyy")
      : "0000";
    this.entityGender = localStorage.getItem("gender")
      ? localStorage.getItem("gender")
      : "NO";
    this.dailyCounterString =
      localStorage.getItem("dailyCounter") &&
      parseInt(localStorage.getItem("dailyCounter")) > 0
        ? this.formatDailyCounter(parseInt(localStorage.getItem("dailyCounter")))
        : "0000";
  }

  private formatDailyCounter(counter: number): string {
    return counter.toString().padStart(4, "0");
  }

  private buildMediaRows() {
    let sourceIndex = 0;
    const exposures = this.adverseMediaData?.news_exposures || {};

    this.categorySections = ADVERSE_MEDIA_CATEGORIES.map((category) => {
      const exposure = exposures[category.key] || {};
      const articles = Array.isArray(exposure.articles) ? exposure.articles : [];
      const categoryRows = articles.map((article) => {
        sourceIndex += 1;
        return {
          categoryKey: category.key,
          categoryLabel: category.label,
          headline: article?.headline || "-",
          language: this.formatLanguage(article?.language),
          date: this.formatArticleDate(article?.timestamp),
          sourceLink: article?.source_link || "",
          sourceLabel: `Source ${sourceIndex.toString().padStart(2, "0")}`,
        } as MediaArticleRow;
      });

      return {
        key: category.key,
        label: category.label,
        emptyMessage: category.emptyMessage,
        hits: categoryRows.length,
        articles: categoryRows,
      };
    });

    this.totalHits = getAdverseMediaTotalReturnedCount(this.adverseMediaData);
  }

  private formatLanguage(language: string): string {
    if (!language) {
      return "-";
    }
    const map: { [code: string]: string } = {
      en: "English",
      "en-us": "English",
      "en-gb": "English",
      zh: "Chinese",
      "zh-cn": "Chinese",
      "zh-tw": "Chinese",
      "zh-hk": "Chinese",
    };
    const key = language.toLowerCase();
    if (map[key]) {
      return map[key];
    }
    return language.charAt(0).toUpperCase() + language.slice(1);
  }

  private formatArticleDate(value: string): string {
    if (!value) {
      return "-";
    }
    const parsed = moment(
      value,
      ["DD/MM/YYYY HH:mm:ss", "DD/MM/YYYY", "YYYY-MM-DD", moment.ISO_8601],
      true,
    );
    if (parsed.isValid()) {
      return parsed.format("DD/MM/YYYY");
    }
    const fallback = moment(value);
    return fallback.isValid() ? fallback.format("DD/MM/YYYY") : value;
  }

  getMatchText(hits: number): string {
    return `${hits} Match${hits > 1 ? "es" : ""}`;
  }

  async getUserDetails() {
    this.userDetails = await this.dataService.getUserDetailsById(
      this.authService.getCurrentUser().userId,
    );
    this.userDetails = this.userDetails[0];
  }

  saveRecord() {
    if (this.isSavingRecord) {
      return;
    }
    this.isSavingRecord = true;
    const currentDate = new Date();
    const historyObj: any = {};
    historyObj["userEmail"] =
      (this.userDetails && this.userDetails.email) ||
      this.authService.getCurrentUser().email;

    const recordJson: any = {
      name: this.searchedName,
      searchedName: this.searchedName,
      timestamp: this.adverseMediaData?.timestamp || "",
      news_exposures: this.adverseMediaData?.news_exposures || {},
      adverseMediaData: this.adverseMediaData,
      totalHits: this.totalHits,
      totalMatches: this.totalHits,
      typeOfSearch: this.typeOfSearch,
      hongKongDT: this.hongKongDT,
      entityGender: this.entityGender,
      entityDD: this.entityDD,
      entityMM: this.entityMM,
      entityYYYY: this.entityYYYY,
      dailyCounterString: this.dailyCounterString,
      todayName: this.todayName,
    };
    recordJson["saveDate"] = moment(
      this.reportDateShow,
      "DD/MM/YYYY HH:mm:ss",
    ).format("DD/MM/YYYY");
    recordJson["saveTime"] = moment(
      this.reportDateShow,
      "DD/MM/YYYY HH:mm:ss",
    ).format("HH:mm:ss");
    recordJson["UTCsaveDate"] = `${currentDate.getUTCDate()}/${
      currentDate.getUTCMonth() + 1
    }/${currentDate.getUTCFullYear()}`;
    recordJson[
      "UTCsaveTime"
    ] = `${currentDate.getUTCHours()}:${currentDate.getUTCMinutes()}:${currentDate.getUTCSeconds()}`;
    recordJson["type"] = "MEDIA_REPORT";

    historyObj["historyData"] = [recordJson];

    this.dataService
      .saveAdverseMediaHistory(historyObj)
      .then((data) => {
        if (data["msg"] == "Saved record") {
          this.isSavingRecord = false;
          this.showSuccessMsg = true;
          setTimeout(() => {
            this.showSuccessMsg = false;
          }, 5000);
        } else {
          this.isSavingRecord = false;
          alert("Failed to save record. Please try after some time");
        }
      })
      .catch((error) => {
        this.isSavingRecord = false;
        if (error && error.statusText && error.statusText.indexOf("Too Large") > -1) {
          alert(
            "The search report file is too large to save in iKYC, we suggest you print or save the report in your local drive, should you have any questions, please feel tree to contact us at info@kcgleads.com, we will get back to you within 24 hours",
          );
        } else if (error && error.statusText) {
          alert(error.statusText);
        } else {
          alert("Failed to save record. Please try after some time");
        }
      });
  }
}
